import { useEffect, useRef, useCallback, useMemo } from "react";
import { useLazyFetchProfileQuery } from "../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import { updateQuickActionMenuData } from "../features/Feature_GeneralAppElements/GeneralAppElements_Slice";
import {
  useAppDispatch,
  useAppSelector,
} from "../redux-store/CustomReduxHooks";
import { setError } from "../redux-store/reducers/CsErrorSlice";
import staticProfileHamburgerMenuData from "../assets/testData/ProfileHamburgerMenuData.json";
import { R_LoginRoutes } from "../utils/Routes";
import { isEmpty, uniq } from "lodash";
import { useLocation } from "react-router-dom";

const useQuickActionMenu = () => {
  const [fetchProfileApi] = useLazyFetchProfileQuery();
  const userDetails = useAppSelector((state) => state.CsAppSlice).userDetails;
  const quickActionMenu = useAppSelector(
    (state) => state.GeneralAppElementsSlice.quickActionMenu,
  );
  const internalAuthToken = useAppSelector(
    (state) => state.CsAppSlice.tokenDetails?.internal,
  );
  const loginSlice = useAppSelector((state) => state.LoginSlice);
  const appDispatch = useAppDispatch();
  const isExecuting = useRef(false);
  // tracks retry attempts per moduleId to stop looping on empty API responses
  const retryCountRef = useRef<Record<string, number>>({});
  const MAX_RETRIES = 2;
  const { pathname } = useLocation();

  // Memoize unique LOB IDs
  const uniqueLobIds = useMemo(() => {
    if (
      !userDetails?.customerAndLobDetails ||
      userDetails.customerAndLobDetails.length === 0
    ) {
      return ["111"];
    }
    const ids = uniq(
      userDetails.customerAndLobDetails
        .map((el) => el?.LOB_CODE)
        .filter(Boolean),
    );
    ids.push("111");
    return ids;
  }, [userDetails?.customerAndLobDetails]);

  const moduleIdsToFetch = useMemo(() => {
    const quickActionMenuMap = (quickActionMenu || {}) as Record<
      string,
      unknown
    >;

    return uniqueLobIds.filter((moduleId) => {
      const moduleData = quickActionMenuMap[moduleId];
      const hasData = Array.isArray(moduleData) && moduleData.length > 0;
      const exhausted = (retryCountRef.current[moduleId] ?? 0) >= MAX_RETRIES;
      return !hasData && !exhausted;
    });
  }, [uniqueLobIds, quickActionMenu]);

  // Memoize fetch profiles function
  const fetchProfiles = useCallback(async () => {
    if (isExecuting.current) return;
    if (moduleIdsToFetch.length === 0) return;

    isExecuting.current = true;

    if (uniqueLobIds.length > 0 && userDetails?.CS_USER_ID) {
      try {
        const profileApiPromises = moduleIdsToFetch.map((element) =>
          fetchProfileApi({
            csUserId: userDetails.CS_USER_ID,
            moduleId: element,
          }),
        );
        const profileResponses = await Promise.all(profileApiPromises);

        let filteredResponse = profileResponses.map((response) => {
          let payload = response.data?.payload?.menuDetailsList;
          const moduleId = response.originalArgs?.moduleId || "";

          if (response.originalArgs?.moduleId === "111") {
            payload = isEmpty(payload)
              ? staticProfileHamburgerMenuData
              : payload;
          }

          // increment retry count for modules that returned empty data
          if (isEmpty(payload) && moduleId) {
            retryCountRef.current[moduleId] =
              (retryCountRef.current[moduleId] ?? 0) + 1;
          }

          return { [moduleId]: payload };
        });

        const responseByLobCode = filteredResponse.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {},
        );

        appDispatch(
          updateQuickActionMenuData({
            ...(quickActionMenu || {}),
            ...responseByLobCode,
          }),
        );
        isExecuting.current = false;
      } catch (err) {
        appDispatch(
          setError({
            error: new Error(),
            message: "failed",
          }),
        );
        isExecuting.current = false;
      }
    } else if (
      userDetails &&
      loginSlice.isUserLoggedIn &&
      moduleIdsToFetch.includes("111")
    ) {
      try {
        const response = await fetchProfileApi({
          csUserId: userDetails?.CS_USER_ID ?? "",
          moduleId: "111",
        });
        const responseByLobCode = {
          "111":
            response.data?.payload &&
            !isEmpty(response.data?.payload?.menuDetailsList)
              ? response.data?.payload?.menuDetailsList
              : staticProfileHamburgerMenuData,
        };
        appDispatch(
          updateQuickActionMenuData({
            ...(quickActionMenu || {}),
            ...responseByLobCode,
          }),
        );
        isExecuting.current = false;
      } catch (err) {
        isExecuting.current = false;
      }
    } else {
      isExecuting.current = false;
    }
  }, [
    moduleIdsToFetch,
    uniqueLobIds,
    userDetails,
    quickActionMenu,
    loginSlice.isUserLoggedIn,
    fetchProfileApi,
    appDispatch,
  ]);

  useEffect(() => {
    if (!pathname.includes(R_LoginRoutes.login)) {
      fetchProfiles();
    }
  }, [
    internalAuthToken,
    uniqueLobIds,
    pathname,
    loginSlice.isUserLoggedIn,
    fetchProfiles,
  ]);
};

export default useQuickActionMenu;
